import { ElevatedSurface } from "@carbon6/c6-frontend-design";
import type { Probe } from "../../domain/probe";
import type { ProbeTableSource } from "../ProbeTableSource";
import { ProbeEvidenceCard } from "./ProbeEvidenceCard";
import { ProbeFailureCard } from "./ProbeFailureCard";
import { ProbeHistoryTable } from "./ProbeHistoryTable";
import { ProbeStateBanner } from "./ProbeStateBanner";
import cardStyles from "./DetailCard.module.css";
import styles from "./ProbeSection.module.css";

type ProbeSectionProps = {
  /** 이력 표의 소스. 페이지가 조회하고 폴링 결과를 흘려 넣는다. */
  source: ProbeTableSource;
  /** 지금 상세로 보여 주는 Probe. 이력이 비면 `null`. */
  probe: Probe | null;
  /** 조회 실패 등 Probe 자체와 무관한 오류. 상태는 마지막으로 본 값을 그대로 둔다. */
  errorMessage: string | null;
  nowMs: number;
  onRetry: () => void;
};

/**
 * Probe 탭의 본문 — 이력 표 + 고른 한 건의 상세.
 *
 * 표는 두 건 이상일 때만 선다. 한 건뿐이면 아래 카드가 이미 그 건을 말하고 있어 고를 것이 없다.
 */
export function ProbeSection({ errorMessage, nowMs, onRetry, probe, source }: ProbeSectionProps) {
  if (probe === null) {
    return (
      <ElevatedSurface
        as="section"
        className={cardStyles.card}
        elevation={1}
        hoverElevation={false}
      >
        <p className={cardStyles.empty}>
          아직 요청한 Probe 가 없습니다. `Probe 요청` 을 누르면 안정적인 순서의 앞 episode 를 샘플로
          확보합니다.
        </p>
      </ElevatedSurface>
    );
  }

  return (
    <div className={styles.section}>
      <ProbeHistoryTable currentProbeUuid={probe.uuid} source={source} />
      <ElevatedSurface
        as="section"
        className={cardStyles.card}
        elevation={1}
        hoverElevation={false}
      >
        <ProbeStateBanner nowMs={nowMs} probe={probe} />
        {errorMessage === null ? null : (
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}
      </ElevatedSurface>
      {probe.state === "sample_ready" ? <ProbeEvidenceCard probe={probe} /> : null}
      {probe.state === "failed" ? <ProbeFailureCard probe={probe} onRetry={onRetry} /> : null}
    </div>
  );
}
